import React from "react";
import { useToastStore, type ToastItem } from "@/store/toastStore";

function icon(type: ToastItem["type"]) {
    // лёгкие inline svg, как на “лучших сайтах”: тонкие, серые, аккуратные
    switch (type) {
        case "success":
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-90">
                    <path
                        d="M20 6L9 17l-5-5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case "error":
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-90">
                    <path
                        d="M12 9v4m0 4h.01M10.3 4.3l-7 12.1A2 2 0 0 0 5 20h14a2 2 0 0 0 1.7-3.6l-7-12.1a2 2 0 0 0-3.4 0Z"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        default:
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-90">
                    <path
                        d="M12 16v-4m0-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
    }
}

function colors(type: ToastItem["type"]) {
    switch (type) {
        case "success":
            return {
                ring: "border-emerald-500/25",
                bg: "bg-emerald-500/10",
                text: "text-emerald-100",
                sub: "text-emerald-200/80",
                icon: "text-emerald-200",
            };
        case "error":
            return {
                ring: "border-red-500/25",
                bg: "bg-red-500/10",
                text: "text-red-100",
                sub: "text-red-200/80",
                icon: "text-red-200",
            };
        default:
            return {
                ring: "border-sky-500/25",
                bg: "bg-sky-500/10",
                text: "text-slate-100",
                sub: "text-slate-300",
                icon: "text-sky-200",
            };
    }
}

export const ToastHost: React.FC = () => {
    const items = useToastStore((s) => s.items);
    const remove = useToastStore((s) => s.remove);

    if (items.length === 0) return null;

    return (
        <div
            className={[
                "fixed z-[9999]",
                "right-3 bottom-3",
                "space-y-2",
                // чтобы в телеге/на айфоне не прилипало к системным зонам
                "pb-[calc(var(--tg-safe-area-inset-bottom,env(safe-area-inset-bottom,0px)))]",
            ].join(" ")}
            aria-live="polite"
            aria-relevant="additions"
        >
            {items.map((t) => {
                const c = colors(t.type);
                return (
                    <div
                        key={t.id}
                        className={[
                            "w-[min(92vw,340px)]",
                            "rounded-2xl border",
                            "bg-slate-950/80 backdrop-blur",
                            "shadow-xl",
                            c.ring,
                        ].join(" ")}
                    >
                        <div className="flex items-start gap-3 p-3">
                            <div className={["mt-0.5", "shrink-0", c.icon].join(" ")}>
                                {icon(t.type)}
                            </div>

                            <div className="min-w-0 flex-1">
                                {t.title && (
                                    <div className={["text-[11px] font-semibold", c.text].join(" ")}>
                                        {t.title}
                                    </div>
                                )}
                                <div className={["text-[11px]", t.title ? c.sub : c.text].join(" ")}>
                                    {t.message}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => remove(t.id)}
                                className={[
                                    "shrink-0",
                                    "rounded-xl border border-slate-800",
                                    "bg-slate-900/40",
                                    "px-2 py-1",
                                    "text-[11px] text-slate-200",
                                    "hover:bg-slate-900/70",
                                ].join(" ")}
                                aria-label="Закрыть"
                                title="Закрыть"
                            >
                                ✕
                            </button>
                        </div>

                        {/* мягкая цветная подложка */}
                        <div className={["h-0.5 rounded-b-2xl", c.bg].join(" ")} />
                    </div>
                );
            })}
        </div>
    );
};
